import { collection, createInterfaceId, Injectable } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import type { CommandComponentRegistry } from '@gitlab-org/tui';
import type { SkillConflict } from '@gitlab-org/ai-context/node';
import type { ControllerApi } from '../commands/tui/controller_api';
import { SlashCommandHandler, type SlashCommand } from './slash_command_handler';
import { DynamicSlashCommandProvider } from './dynamic_slash_command_provider';

export interface DynamicProvidersRegistrationResult {
  warnings: string[];
  conflicts: SkillConflict[];
}

export interface SearchResult {
  name: string;
  displayName: string;
  description: string;
}

export interface SlashCommandService {
  getCommands(): SlashCommand[];

  /**
   * Commands whose handler implements `toPrompt` (a headless prompt form);
   * the subset safe to advertise to non-interactive clients (e.g. ACP's
   * `available_commands_update`). Excludes interactive-only commands like
   * `/settings` or `/exit`.
   */
  getHeadlessCommands(): SlashCommand[];

  isCommand(prompt: string): boolean;

  execute(prompt: string, api: ControllerApi): Promise<void>;

  /**
   * Derives the prompt that the given input would produce when executed
   * non-interactively (headless). Returns undefined when the input is not a
   * known command or the command has no headless prompt form.
   */
  toPrompt(prompt: string): Promise<string | undefined>;

  searchCommands(query: string): SearchResult[];

  /**
   * Registers dynamic commands, skipping any whose name is invalid or
   * conflicts with an existing command. Returns user-facing warnings for
   * commands skipped due to name conflicts.
   */
  registerDynamicCommands(commands: SlashCommand[]): string[];

  /**
   * Registers commands from all DynamicSlashCommandProvider implementations.
   * Providers are processed in descending priority order, so higher-priority
   * providers win on name collisions. Intended to be called once during
   * controller initialisation. Returns user-facing warnings and skill
   * conflicts aggregated from all providers.
   */
  registerDynamicProviders(): Promise<DynamicProvidersRegistrationResult>;

  removeDynamicCommands(): void;

  /**
   * Builds a CommandComponentRegistry by collecting each handler's registered
   * component and callbacks, keyed by the handler's inputType.
   * Used by TUIController to pass the registry to the TUI App.
   */
  buildComponentRegistry(api: ControllerApi): CommandComponentRegistry;
}

export const SlashCommandService = createInterfaceId<SlashCommandService>('SlashCommandService');

@Injectable(SlashCommandService, [
  Logger,
  collection(SlashCommandHandler),
  collection(DynamicSlashCommandProvider),
])
export class DefaultSlashCommandService implements SlashCommandService {
  #logger: Logger;

  #commands: Map<string, SlashCommand> = new Map();

  #dynamicCommands: Map<string, SlashCommand> = new Map();

  #aliases: Map<string, string> = new Map();

  #handlers: Map<string, SlashCommandHandler> = new Map();

  #dynamicProviders: DynamicSlashCommandProvider[];

  constructor(
    logger: Logger,
    handlers: SlashCommandHandler[],
    dynamicProviders: DynamicSlashCommandProvider[],
  ) {
    this.#logger = withPrefix(logger, '[SlashCommandService]');
    this.#dynamicProviders = [...dynamicProviders].sort((a, b) => b.priority - a.priority);
    this.#registerHandlers(handlers);
  }

  #registerHandlers(handlers: SlashCommandHandler[]): void {
    handlers.forEach((handler) => {
      this.#evictCollidingAlias(handler.command.name);
      this.#commands.set(handler.command.name, handler.command);
      this.#handlers.set(handler.command.action, handler);
      this.#registerAliases(handler.command);
      this.#logger.debug(
        `Registered slash command: ${handler.command.name} (action: ${handler.command.action})`,
      );
    });
  }

  // Drop any alias whose key equals this command name. Symmetric to the
  // collision check in #registerAliases — without it, an alias registered
  // before its colliding command lingers in the map and surfaces as a
  // misleading display hint in the autocomplete dropdown.
  #evictCollidingAlias(name: string): void {
    const owner = this.#aliases.get(name);
    if (owner !== undefined) {
      this.#logger.warn(
        `Dropping alias "${name}" of ${owner}: conflicts with command name registered later`,
      );
      this.#aliases.delete(name);
    }
  }

  #registerAliases(command: SlashCommand): void {
    command.aliases?.forEach((alias) => {
      if (!alias.startsWith('/') || /\s/.test(alias)) {
        this.#logger.warn(
          `Skipping alias "${alias}" for ${command.name}: invalid name (must start with / and contain no whitespace)`,
        );
        return;
      }
      if (this.#commands.has(alias) || this.#dynamicCommands.has(alias)) {
        this.#logger.warn(
          `Skipping alias "${alias}" for ${command.name}: conflicts with a command name`,
        );
        return;
      }
      const aliasOwner = this.#aliases.get(alias);
      if (aliasOwner !== undefined) {
        // Re-registering the same alias for the same command (providers are
        // re-run on refresh) is a no-op, not a collision.
        if (aliasOwner !== command.name) {
          this.#logger.warn(`Skipping alias "${alias}" for ${command.name}: alias already in use`);
        }
        return;
      }
      this.#aliases.set(alias, command.name);
    });
  }

  #resolveCommandName(name: string): string {
    if (this.#commands.has(name) || this.#dynamicCommands.has(name)) return name;
    return this.#aliases.get(name) ?? name;
  }

  #tokenize(prompt: string): string[] {
    return prompt.trim().split(/\s+/);
  }

  getCommands(): SlashCommand[] {
    return [
      ...Array.from(this.#commands.values()),
      ...Array.from(this.#dynamicCommands.values()),
    ].filter((command) => !command.internal);
  }

  getHeadlessCommands(): SlashCommand[] {
    return this.getCommands().filter(
      (command) => this.#handlers.get(command.action)?.toPrompt !== undefined,
    );
  }

  isCommand(prompt: string): boolean {
    const trimmed = prompt.trim();

    if (!trimmed.startsWith('/')) {
      return false;
    }

    const parts = trimmed.split(/\s+/);
    const commandName = this.#resolveCommandName(parts[0]);

    return this.#commands.has(commandName) || this.#dynamicCommands.has(commandName);
  }

  // Resolves an input string to its handler and the args to pass that handler.
  // Returns undefined when the input is not a known command. Shared by execute()
  // and toPrompt() so the command/alias/dynamic-arg resolution stays in one place.
  #resolve(
    prompt: string,
  ): { command: SlashCommand; handler: SlashCommandHandler; effectiveArgs?: string[] } | undefined {
    const [first, ...args] = this.#tokenize(prompt);
    const commandName = this.#resolveCommandName(first);

    const isDynamic = this.#dynamicCommands.has(commandName);
    const command = this.#commands.get(commandName) || this.#dynamicCommands.get(commandName);
    if (!command) {
      return undefined;
    }

    const handler = this.#handlers.get(command.action);
    if (!handler) {
      throw new Error(`No handler registered for action: ${command.action}`);
    }

    let effectiveArgs: string[] | undefined;
    if (isDynamic) {
      effectiveArgs = [commandName.slice(1), ...args];
    } else if (args.length > 0) {
      effectiveArgs = args;
    }

    return { command, handler, effectiveArgs };
  }

  async execute(prompt: string, api: ControllerApi): Promise<void> {
    const resolved = this.#resolve(prompt);
    if (!resolved) {
      throw new Error(`Unknown command: ${this.#tokenize(prompt)[0]}`);
    }

    const { command, handler, effectiveArgs } = resolved;
    this.#logger.info(`Executing slash command: ${command.name} (action: ${command.action})`);
    await handler.execute(api, effectiveArgs);
  }

  async toPrompt(prompt: string): Promise<string | undefined> {
    const resolved = this.#resolve(prompt);
    if (!resolved) {
      return undefined;
    }
    return resolved.handler.toPrompt?.(resolved.effectiveArgs);
  }

  registerDynamicCommands(commands: SlashCommand[]): string[] {
    const warnings: string[] = [];
    commands.forEach((command) => {
      if (!command.name.startsWith('/') || /\s/.test(command.name)) {
        this.#logger.warn(
          `Skipping dynamic command "${command.name}": invalid name (must start with / and contain no whitespace)`,
        );
        return;
      }
      if (this.#commands.has(command.name)) {
        warnings.push(`Skipped command "${command.name}": conflicts with a built-in command`);
        this.#logger.debug(
          `Skipping dynamic command ${command.name}: conflicts with built-in command`,
        );
        return;
      }
      if (this.#dynamicCommands.has(command.name)) {
        // Dynamic providers register in descending priority order (skills >
        // custom commands > plugin commands), so the earlier registration is
        // always the higher-priority one — but which pair collided varies.
        warnings.push(
          `Skipped command "${command.name}": a higher-priority command with the same name is already registered`,
        );
        this.#logger.debug(
          `Skipping dynamic command ${command.name}: conflicts with an already-registered dynamic command`,
        );
        return;
      }
      // Built-in aliases must win too: dynamic sources include third-party
      // plugin commands, and letting one take over e.g. `/quit` (alias of
      // `/exit`) would silently reroute a built-in to an untrusted prompt.
      const aliasOwner = this.#aliases.get(command.name);
      if (aliasOwner !== undefined) {
        warnings.push(
          `Skipped command "${command.name}": conflicts with an alias of the built-in command ${aliasOwner}`,
        );
        this.#logger.debug(
          `Skipping dynamic command ${command.name}: conflicts with alias of ${aliasOwner}`,
        );
        return;
      }
      this.#dynamicCommands.set(command.name, command);
      this.#logger.debug(`Registered dynamic slash command: ${command.name}`);
    });
    // Aliases in a second pass so a short alias can never pre-empt a canonical
    // command name from the same batch. #registerAliases skips aliases that
    // collide with any command name or an already-claimed alias, so the loser
    // of a collision stays reachable under its canonical name.
    commands.forEach((command) => {
      if (this.#dynamicCommands.get(command.name) === command) {
        this.#registerAliases(command);
      }
    });
    return warnings;
  }

  async registerDynamicProviders(): Promise<DynamicProvidersRegistrationResult> {
    const allWarnings: string[] = [];
    const allConflicts: SkillConflict[] = [];
    for (const provider of this.#dynamicProviders) {
      try {
        // Sequential so providers register in priority order (higher priority wins collisions).
        // eslint-disable-next-line no-await-in-loop
        const { commands, warnings, conflicts } = await provider.getCommands();
        allWarnings.push(...warnings);
        allConflicts.push(...(conflicts ?? []));
        if (commands.length > 0) {
          allWarnings.push(...this.registerDynamicCommands(commands));
          this.#logger.debug(
            `Registered ${commands.length} dynamic slash commands: ${commands.map((c) => c.name).join(', ')}`,
          );
        }
      } catch (error) {
        this.#logger.warn('Dynamic slash command provider failed - skipping', error);
      }
    }
    return { warnings: allWarnings, conflicts: allConflicts };
  }

  removeDynamicCommands(): void {
    // Dynamic commands can own aliases (e.g. the bare `/<name>` of a
    // namespaced plugin command); leaving them behind would resolve to a
    // command that no longer exists.
    for (const [alias, owner] of this.#aliases) {
      if (this.#dynamicCommands.has(owner)) {
        this.#aliases.delete(alias);
      }
    }
    this.#dynamicCommands.clear();
  }

  buildComponentRegistry(api: ControllerApi): CommandComponentRegistry {
    const registry: CommandComponentRegistry = new Map();
    for (const handler of this.#handlers.values()) {
      if (handler.getComponent) {
        const { inputType, component, callbacks, footerHint } = handler.getComponent(api);
        registry.set(inputType, { component, callbacks, footerHint });
      }
    }
    return registry;
  }

  searchCommands(query: string): SearchResult[] {
    const allCommands = this.getCommands();

    if (!query) {
      return allCommands
        .map((cmd) => this.#createSearchResult(cmd))
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    const lowerQuery = this.#stripSlash(query).toLowerCase();
    const descRegex = this.#buildDescriptionRegex(lowerQuery);

    return allCommands
      .map((cmd) => this.#scoreCommand(cmd, lowerQuery, descRegex))
      .filter(({ score }) => score !== Infinity)
      .sort(this.#compareCommandResults)
      .map(({ cmd, matchedAlias }) => this.#createSearchResult(cmd, matchedAlias));
  }

  #stripSlash(s: string): string {
    return s.startsWith('/') ? s.slice(1) : s;
  }

  // Single-character queries should only match command names; longer queries
  // fall through to a word-boundary description match.
  #buildDescriptionRegex(lowerQuery: string): RegExp | null {
    if (lowerQuery.length < 2) return null;
    return new RegExp(`(?:^|\\s)${lowerQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
  }

  #scoreCommand(
    cmd: SlashCommand,
    lowerQuery: string,
    descRegex: RegExp | null,
  ): { cmd: SlashCommand; score: number; matchedAlias?: string } {
    const nameMatch = this.#stripSlash(cmd.name).toLowerCase();
    const descMatch = cmd.description.toLowerCase();

    const nameMatches = nameMatch.startsWith(lowerQuery);
    // Only look up an alias when the canonical name doesn't already prefix-match,
    // and only consider aliases that resolved to *this* command at registration —
    // rejected/colliding aliases must not surface as display hints.
    const matchedAlias = nameMatches
      ? undefined
      : this.#findBestMatchingAlias(this.#registeredAliasesFor(cmd), lowerQuery);

    // Calculate priority score (lower = better)
    let score = Infinity;

    if (nameMatches || matchedAlias !== undefined) {
      // Prefix match on name or alias (highest priority)
      score = 0;
    } else if (nameMatch.includes(lowerQuery)) {
      // Substring match on name
      score = 1;
    } else if (descRegex !== null && descRegex.test(descMatch)) {
      // Description word-boundary match (lowest priority)
      score = 2;
    }

    return { cmd, score, matchedAlias };
  }

  #registeredAliasesFor(cmd: SlashCommand): readonly string[] | undefined {
    return cmd.aliases?.filter((alias) => this.#aliases.get(alias) === cmd.name);
  }

  // Shortest match wins: longer aliases sharing the prefix surface as the user types more.
  #findBestMatchingAlias(
    aliases: readonly string[] | undefined,
    lowerQuery: string,
  ): string | undefined {
    return aliases
      ?.filter((alias) => this.#stripSlash(alias).toLowerCase().startsWith(lowerQuery))
      .sort((a, b) => this.#stripSlash(a).length - this.#stripSlash(b).length)[0];
  }

  #compareCommandResults(
    a: { cmd: SlashCommand; score: number },
    b: { cmd: SlashCommand; score: number },
  ): number {
    // Sort by score, then alphabetically by name
    if (a.score !== b.score) return a.score - b.score;
    return a.cmd.name.localeCompare(b.cmd.name);
  }

  #formatCommandName(cmd: SlashCommand, matchedAlias?: string): string {
    if (matchedAlias === undefined) return cmd.name;
    return `${cmd.name} (${this.#stripSlash(matchedAlias)})`;
  }

  #createSearchResult(cmd: SlashCommand, matchedAlias?: string): SearchResult {
    return {
      name: cmd.name,
      displayName: this.#formatCommandName(cmd, matchedAlias),
      description: cmd.description,
    };
  }
}
